/**
 * Elite Batch 5 Part 3 — Workflow Templates 388–407
 * Email Verification, First-Order Lifecycle, Flash Sales, Gift Commerce,
 * Google Merchant Sync, Churn Rescue, High-AOV Recovery, High-Intent Concierge
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function shopifyNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://{shopify_store}.myshopify.com/admin/api/2024-01/${endpoint}`,
        headers: [
          { key: "X-Shopify-Access-Token", value: "{shopify_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 5 PART 3 — TEMPLATES 388–407
// =============================================================================

export const ELITE_BATCH5_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 388. EMAIL VERIFICATION REMINDER (7 nodes)
  // ===================================================================

  "Email Verification Reminder": {
    summary:
      "Guides new accounts through email verification with progressive urgency. Account creation triggers a verification email, waits 24 hours, checks verification status, then sends a reminder or final warning before restricting the account and logging the outcome.",
    nodes: [
      trigger("Webhook: Account creation", "webhook_app", {
        source: "Auth Service / Signup Form",
        event: "user.created",
        howItStarts:
          "A new user account is created and email verification is pending",
        setup:
          "Connect your authentication service or signup form to POST new account data (user ID, email, signup timestamp) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Verification email
      emailNode(
        "2",
        "Send verification email",
        280,
        "{user_email}",
        "Verify your email to activate your account",
        "Welcome! Please verify your email address to unlock full access to your account.\\n\\nClick here to verify: {verification_link}\\n\\nThis link expires in 48 hours. If you did not create this account, please ignore this email.",
      ),

      // 3. 24-hour delay
      n("3", "delayAction", "Wait 24 hours for verification", 520, {
        delayValue: 24,
        delayUnit: "hours",
      }),

      // 4. Verification status check
      n("4", "condition", "Email verified?", 760, {
        conditionData: [
          {
            variable: "email_verified",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Reminder email (not verified — True path skips to logging)
      emailNode(
        "5",
        "Send reminder: Verify to keep access",
        1000,
        "{user_email}",
        "Reminder: Verify your email within 24 hours",
        "Hi {user_name},\\n\\nYour email is still unverified. Without verification, your account will be restricted in 24 hours.\\n\\nVerify now: {verification_link}\\n\\nNeed help? Reply to this email and we will assist you.",
      ),

      // 6. Final reminder + account restriction warning
      emailNode(
        "6",
        "Final warning: Account restriction imminent",
        1240,
        "{user_email}",
        "Final notice: Your account will be restricted today",
        "This is your final reminder. Your account will be restricted to read-only mode if you do not verify your email within the next 6 hours.\\n\\nVerify now: {verification_link}\\n\\nIf you are having trouble, contact support at {support_email}.",
      ),

      // 7. Log verification outcome
      sheetsNode(
        "7",
        "Log verification outcome to Sheets",
        1480,
        "VerificationLog",
        '[["{user_email}","{user_id}","{email_verified}","{signup_timestamp}","{reminder_count}","{final_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"), // verified -> log success
      e("4", "5", "output_2"), // not verified -> reminder
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 389. FIRST ORDER DELIVERED NO REPEAT IN 30 DAYS (8 nodes)
  // ===================================================================

  "First Order Delivered No Repeat in 30 Days": {
    summary:
      "Re-engages first-time buyers who have not reordered within 30 days of delivery. A scheduled scan identifies lapsed first-buyers, checks for repeat purchases, uses AI to analyze preferences and generate personalized re-engagement copy, sends an incentive email, follows up with SMS for non-openers, and logs category insights.",
    nodes: [
      trigger("Schedule: 30-day post-delivery scan", "schedule", {
        schedule: "Daily at 09:00 UTC",
        howItStarts:
          "Runs daily to find customers whose first order was delivered exactly 30 days ago with no subsequent purchase",
        setup:
          "Enable this scheduled workflow. It queries your order database for first-time buyers whose delivery date was 30 days ago and who have not placed a second order.",
        inputFields: [],
      }),

      // 2. Repeat check condition
      n("2", "condition", "Has repeat purchase?", 280, {
        conditionData: [
          {
            variable: "repeat_order_count",
            value: "0",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3. AI purchase analysis + re-engagement copy
      aiNode(
        "3",
        "AI analyze purchase and generate re-engagement",
        520,
        "You are a retention marketing specialist. Analyze this first-time buyer who has not reordered in 30 days. Given their original order details (products, category, price), infer their preferences and generate compelling re-engagement content. Include a personalized recommendation, a reason to return, and a subject line. Respond JSON: {subject_line: string, email_body: string, recommended_products: string, return_reason: string, inferred_category_preference: string, incentive_type: string, sms_nudge: string}",
        "Customer: {customer_email} | First order: {first_order_products} | Category: {first_order_category} | Order value: {first_order_total} | Delivery date: {delivery_date} | Days since delivery: 30",
        [
          { field: "subject_line", as: "subject_line", type: "string" },
          { field: "email_body", as: "email_body", type: "string" },
          {
            field: "recommended_products",
            as: "recommended_products",
            type: "string",
          },
          { field: "return_reason", as: "return_reason", type: "string" },
          {
            field: "inferred_category_preference",
            as: "inferred_category_preference",
            type: "string",
          },
          { field: "incentive_type", as: "incentive_type", type: "string" },
          { field: "sms_nudge", as: "sms_nudge", type: "string" },
        ],
      ),

      // 4. Re-engagement email with incentive
      emailNode(
        "4",
        "Re-engagement email with incentive",
        760,
        "{customer_email}",
        "{subject_line}",
        "{email_body}",
      ),

      // 5. Incentive eligibility condition
      n("5", "condition", "Eligible for discount incentive?", 1000, {
        conditionData: [
          {
            variable: "incentive_type",
            value: "none",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. SMS for non-openers via Twilio
      n("6", "sendSMS", "SMS re-engagement nudge (Twilio)", 1240, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message: "{sms_nudge}",
        },
      }),

      // 7. Second-order logging (Sheets)
      sheetsNode(
        "7",
        "Log re-engagement to Sheets",
        1480,
        "FirstBuyerRetention",
        '[["{customer_email}","{first_order_category}","{first_order_total}","{inferred_category_preference}","{incentive_type}","{recommended_products}","{return_reason}"]]',
      ),

      // 8. Category comparison analysis
      n("8", "runCode", "Category comparison analysis", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Aggregate category preference data for first-order retention insights
const category = vars.inferred_category_preference || "unknown";
const orderValue = parseFloat(vars.first_order_total) || 0;
const incentive = vars.incentive_type || "none";
const result = {
  retention_segment: orderValue > 100 ? "high_value_first_buyer" : orderValue > 50 ? "mid_value_first_buyer" : "low_value_first_buyer",
  category_retention_tag: category + "_30d_lapsed",
  recommended_followup_days: orderValue > 100 ? 7 : 14,
  incentive_used: incentive !== "none" ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "retention_segment", type: "string" },
            { name: "category_retention_tag", type: "string" },
            { name: "recommended_followup_days", type: "number" },
            { name: "incentive_used", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // no repeat purchase -> re-engage
      e("2", "7", "output_2"), // has repeat -> just log
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // eligible for incentive -> SMS
      e("5", "7", "output_2"), // not eligible -> skip SMS, log
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 390. FIRST ORDER DELIVERED NO REVIEW (7 nodes)
  // ===================================================================

  "First Order Delivered No Review": {
    summary:
      "Prompts first-time buyers to leave a review after delivery. Waits for review timing, checks if a review already exists, generates a personalized first-buyer review request via AI, offers a review incentive if eligible, suppresses on organic review, and logs review rates.",
    nodes: [
      trigger("Webhook: First order delivered", "webhook_app", {
        source: "Shopify / Fulfillment Service",
        event: "order.delivered",
        howItStarts:
          "Fires when a first-time customer's order is marked as delivered by the fulfillment service",
        setup:
          "Connect your Shopify store or fulfillment service to POST delivery confirmation data (order ID, customer email, products, delivery date, order count) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Review timing delay (5 days post-delivery)
      n("2", "delayAction", "Wait 5 days for product use", 280, {
        delayValue: 5,
        delayUnit: "days",
      }),

      // 3. Review exists condition
      n("3", "condition", "Review already submitted?", 520, {
        conditionData: [
          {
            variable: "review_exists",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI first-buyer review request
      aiNode(
        "4",
        "AI personalized review request",
        760,
        "You are a customer engagement specialist crafting a review request for a first-time buyer. Make it warm, personal, and emphasize how much their feedback matters as a new customer. Reference the specific product they bought. Include a short subject line and email body. Respond JSON: {subject_line: string, email_body: string, product_highlight: string, review_cta: string, sms_reminder: string}",
        "Customer: {customer_name} ({customer_email}) | Product: {product_name} | Category: {product_category} | Order value: {order_total} | Delivery date: {delivery_date} | First order: yes",
        [
          { field: "subject_line", as: "review_subject", type: "string" },
          { field: "email_body", as: "review_email_body", type: "string" },
          {
            field: "product_highlight",
            as: "product_highlight",
            type: "string",
          },
          { field: "review_cta", as: "review_cta", type: "string" },
          { field: "sms_reminder", as: "sms_reminder", type: "string" },
        ],
      ),

      // 5. Review request email
      emailNode(
        "5",
        "Send first-buyer review request",
        1000,
        "{customer_email}",
        "{review_subject}",
        "{review_email_body}",
      ),

      // 6. Review incentive if allowed
      n("6", "condition", "Incentive-eligible first buyer?", 1240, {
        conditionData: [
          {
            variable: "incentive_eligible",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. First-buyer review rate logging (Sheets)
      sheetsNode(
        "7",
        "Log first-buyer review rate",
        1480,
        "FirstBuyerReviews",
        '[["{customer_email}","{product_name}","{product_category}","{order_total}","{review_exists}","{incentive_eligible}","{delivery_date}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "7", "output_1"), // review exists -> suppress, just log
      e("3", "4", "output_2"), // no review -> request
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // incentive eligible -> log with incentive flag
      e("6", "7", "output_2"), // not eligible -> log normally
    ],
  },

  // ===================================================================
  // 391. FIRST-TIME BROWSER RECOVERY (8 nodes)
  // ===================================================================

  "First-Time Browser Recovery": {
    summary:
      "Recovers first-time identified visitors who exit without engagement. Checks identity confidence, assembles brand story and trust content via AI, generates a personalized welcome-offer recovery email, sends it with optional incentive, enriches the visitor in HubSpot CRM, and logs first-visitor recovery metrics.",
    nodes: [
      trigger("Webhook: First-time visitor exit", "webhook_app", {
        source: "Website Analytics / CDP",
        event: "visitor.first_identified_exit",
        howItStarts:
          "A first-time identified visitor exits the site without adding to cart or engaging with key content",
        setup:
          "Connect your website analytics or CDP to POST first-time visitor exit data (visitor ID, email, pages viewed, time on site, exit page, referral source) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Identity confidence condition
      n("2", "condition", "Identity confidence sufficient?", 280, {
        conditionData: [
          {
            variable: "identity_confidence",
            value: "0.6",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 3. Brand story + trust content assembly
      n("3", "runCode", "Assemble brand story + trust signals", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build trust content package based on referral source and pages viewed
const source = vars.referral_source || "direct";
const pagesViewed = parseInt(vars.pages_viewed) || 1;
const trustSignals = [];
trustSignals.push("Free shipping on first order");
trustSignals.push("30-day hassle-free returns");
trustSignals.push("4.8/5 average customer rating");
if (source === "social") trustSignals.push("Trusted by 50K+ followers");
if (source === "search") trustSignals.push("Top-rated in category");
const welcomeOffer = pagesViewed >= 3 ? "15% off first order" : "10% off first order";
const result = {
  trust_package: trustSignals.join(" | "),
  welcome_offer: welcomeOffer,
  offer_code: pagesViewed >= 3 ? "WELCOME15" : "WELCOME10",
  visitor_engagement_level: pagesViewed >= 5 ? "high" : pagesViewed >= 3 ? "medium" : "low",
  referral_type: source
};
return result;`,
          outputVariables: [
            { name: "trust_package", type: "string" },
            { name: "welcome_offer", type: "string" },
            { name: "offer_code", type: "string" },
            { name: "visitor_engagement_level", type: "string" },
            { name: "referral_type", type: "string" },
          ],
        },
      }),

      // 4. AI brand-intro + browse recovery copy
      aiNode(
        "4",
        "AI brand intro + recovery copy",
        760,
        "You are a brand storytelling specialist. Write a warm, welcoming browse-recovery email for a first-time visitor. Include brand story elements, trust signals, and a gentle invitation to return. Reference what they browsed. Make it feel personal, not salesy. Respond JSON: {subject_line: string, email_body: string, hero_message: string, brand_story_snippet: string}",
        "Visitor: {visitor_email} | Pages viewed: {pages_viewed} | Exit page: {exit_page} | Referral: {referral_type} | Trust signals: {trust_package} | Welcome offer: {welcome_offer} | Offer code: {offer_code} | Engagement: {visitor_engagement_level}",
        [
          { field: "subject_line", as: "recovery_subject", type: "string" },
          { field: "email_body", as: "recovery_body", type: "string" },
          { field: "hero_message", as: "hero_message", type: "string" },
          {
            field: "brand_story_snippet",
            as: "brand_story_snippet",
            type: "string",
          },
        ],
      ),

      // 5. Welcome offer condition
      n("5", "condition", "Welcome offer allowed?", 1000, {
        conditionData: [
          {
            variable: "offer_code",
            value: "",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Recovery email
      emailNode(
        "6",
        "Send brand intro recovery email",
        1240,
        "{visitor_email}",
        "{recovery_subject}",
        "{recovery_body}\\n\\nUse code {offer_code} for {welcome_offer}.\\n\\n{trust_package}",
      ),

      // 7. CRM enrichment (HubSpot)
      n("7", "hitApi", "Enrich visitor in HubSpot CRM", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{visitor_email}","first_visit_source":"{referral_type}","pages_viewed":"{pages_viewed}","engagement_level":"{visitor_engagement_level}","welcome_offer_sent":"{offer_code}","lifecyclestage":"lead"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      // 8. First-visitor logging (Sheets)
      sheetsNode(
        "8",
        "Log first-visitor recovery to Sheets",
        1720,
        "FirstVisitorRecovery",
        '[["{visitor_email}","{referral_type}","{pages_viewed}","{visitor_engagement_level}","{offer_code}","{identity_confidence}","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // confidence sufficient -> proceed
      e("2", "8", "output_2"), // low confidence -> just log
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // offer allowed -> send email
      e("5", "7", "output_2"), // no offer -> still enrich CRM
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 392. FIRST-TIME BUYER CART RECOVERY (9 nodes)
  // ===================================================================

  "First-Time Buyer Cart Recovery": {
    summary:
      "Recovers abandoned carts specifically for first-time buyers with trust-building content. Detects first-order status, assembles social proof and trust signals, generates reassurance copy via AI, creates a welcome discount, sends a trust-focused recovery email, follows up with SMS testimonials, and feeds acquisition learning data.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "cart.abandoned",
        howItStarts:
          "A cart is abandoned and the customer is identified as a first-time buyer (no prior orders)",
        setup:
          "Connect your Shopify store or e-commerce platform to POST cart abandonment data (cart ID, customer email, cart items, cart total, customer order count) to the workflow URL.",
        inputFields: [],
      }),

      // 2. First-order check condition
      n("2", "condition", "First-time buyer (zero orders)?", 280, {
        conditionData: [
          {
            variable: "customer_order_count",
            value: "0",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3. Trust content assembly (runCode)
      n("3", "runCode", "Assemble trust content package", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build first-buyer trust stack for cart recovery
const cartTotal = parseFloat(vars.cart_total) || 0;
const trustElements = [
  "Free returns within 30 days",
  "Secure checkout (256-bit SSL)",
  "4.8/5 stars from 10,000+ reviews"
];
if (cartTotal > 100) {
  trustElements.push("Free express shipping on orders over $100");
  trustElements.push("Price match guarantee");
}
const testimonials = [
  "I was hesitant at first, but the quality blew me away! - Sarah K.",
  "Best purchase I have made online this year. Fast shipping too! - Mike R.",
  "Customer service went above and beyond when I had a question. - Lisa T."
];
const discountPct = cartTotal > 150 ? 15 : cartTotal > 75 ? 12 : 10;
const result = {
  trust_stack: trustElements.join(" | "),
  featured_testimonial: testimonials[Math.floor(Math.random() * testimonials.length)],
  discount_percentage: discountPct,
  discount_code: "FIRST" + discountPct,
  cart_value_tier: cartTotal > 150 ? "high" : cartTotal > 75 ? "medium" : "low"
};
return result;`,
          outputVariables: [
            { name: "trust_stack", type: "string" },
            { name: "featured_testimonial", type: "string" },
            { name: "discount_percentage", type: "number" },
            { name: "discount_code", type: "string" },
            { name: "cart_value_tier", type: "string" },
          ],
        },
      }),

      // 4. AI reassurance copy
      aiNode(
        "4",
        "AI generate trust-based recovery copy",
        760,
        "You are an e-commerce conversion specialist focused on first-time buyer anxiety. Write a cart recovery email that leads with trust and reassurance, not urgency. Include the testimonial naturally, highlight the trust signals, and mention the welcome discount. Make it feel like a personal note from the brand. Respond JSON: {subject_line: string, email_body: string, sms_testimonial: string, reassurance_headline: string}",
        "Customer: {customer_email} | Cart items: {cart_items} | Cart total: {cart_total} | Trust signals: {trust_stack} | Testimonial: {featured_testimonial} | Discount: {discount_percentage}% with code {discount_code} | Tier: {cart_value_tier}",
        [
          { field: "subject_line", as: "recovery_subject", type: "string" },
          { field: "email_body", as: "recovery_body", type: "string" },
          { field: "sms_testimonial", as: "sms_testimonial", type: "string" },
          {
            field: "reassurance_headline",
            as: "reassurance_headline",
            type: "string",
          },
        ],
      ),

      // 5. Welcome discount code generation
      n("5", "runCode", "Generate unique welcome discount code", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a unique discount code for first-time buyer recovery
const baseCode = vars.discount_code || "FIRST10";
const uniqueSuffix = Date.now().toString(36).toUpperCase().slice(-4);
const result = {
  unique_discount_code: baseCode + "-" + uniqueSuffix,
  discount_expiry: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString().split("T")[0],
  discount_value: vars.discount_percentage + "%"
};
return result;`,
          outputVariables: [
            { name: "unique_discount_code", type: "string" },
            { name: "discount_expiry", type: "string" },
            { name: "discount_value", type: "string" },
          ],
        },
      }),

      // 6. Trust-stack recovery email
      emailNode(
        "6",
        "Send trust-focused recovery email",
        1240,
        "{customer_email}",
        "{recovery_subject}",
        "{recovery_body}\\n\\nYour exclusive welcome code: {unique_discount_code} ({discount_value} off, expires {discount_expiry})\\n\\n{trust_stack}",
      ),

      // 7. SMS testimonial (Twilio)
      n("7", "sendSMS", "SMS testimonial + cart link (Twilio)", 1480, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message:
            "{sms_testimonial} Complete your order: {cart_recovery_link} Code: {unique_discount_code}",
        },
      }),

      // 8. First-buyer recovery logging
      sheetsNode(
        "8",
        "Log first-buyer recovery to Sheets",
        1720,
        "FirstBuyerRecovery",
        '[["{customer_email}","{cart_total}","{cart_value_tier}","{discount_value}","{unique_discount_code}","{reassurance_headline}","{featured_testimonial}"]]',
      ),

      // 9. Acquisition learning feed
      n("9", "runCode", "Feed acquisition learning model", 1960, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile acquisition learning data from first-buyer recovery
const result = {
  learning_event: "first_buyer_cart_recovery",
  cart_tier: vars.cart_value_tier || "unknown",
  discount_offered: vars.discount_value || "0%",
  trust_approach: "testimonial_led",
  recovery_channel: "email_plus_sms",
  timestamp: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "learning_event", type: "string" },
            { name: "cart_tier", type: "string" },
            { name: "recovery_channel", type: "string" },
            { name: "timestamp", type: "string" },
          ],
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3", "output_1"), // first-time buyer -> trust recovery
      e("2", "8", "output_2"), // returning buyer -> just log (different flow)
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 393. FIRST-TIME BUYER NEVER BOUGHT AGAIN (8 nodes)
  // ===================================================================

  "First-Time Buyer Never Bought Again": {
    summary:
      "Reactivates one-time buyers who never returned. Scans for single-purchase customers, analyzes their purchase history, generates AI recommendations, enriches with live Shopify stock/price data, sends a reactivation email, follows up via SMS, alerts the retention team on Slack, and logs outcomes.",
    nodes: [
      trigger("Schedule: One-time buyer scan", "schedule", {
        schedule: "Weekly on Monday at 08:00 UTC",
        howItStarts:
          "Runs weekly to identify customers with exactly one order placed more than 60 days ago and no subsequent activity",
        setup:
          "Enable this scheduled workflow. It queries your customer database for single-purchase customers whose order was more than 60 days ago.",
        inputFields: [],
      }),

      // 2. Purchase analysis
      n("2", "runCode", "Analyze one-time buyer purchase", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze one-time buyer data for reactivation targeting
const orderValue = parseFloat(vars.first_order_total) || 0;
const daysSince = parseInt(vars.days_since_order) || 60;
const category = vars.first_order_category || "general";
const result = {
  value_segment: orderValue > 100 ? "high_value" : orderValue > 50 ? "mid_value" : "entry_level",
  urgency_level: daysSince > 120 ? "critical" : daysSince > 90 ? "high" : "moderate",
  reactivation_offer: orderValue > 100 ? "20% off next order" : "15% off next order",
  offer_code: orderValue > 100 ? "COMEBACK20" : "COMEBACK15",
  preferred_category: category,
  days_lapsed: daysSince
};
return result;`,
          outputVariables: [
            { name: "value_segment", type: "string" },
            { name: "urgency_level", type: "string" },
            { name: "reactivation_offer", type: "string" },
            { name: "offer_code", type: "string" },
            { name: "preferred_category", type: "string" },
            { name: "days_lapsed", type: "number" },
          ],
        },
      }),

      // 3. AI recommendation generation
      aiNode(
        "3",
        "AI generate personalized recommendations",
        520,
        "You are a retention specialist for e-commerce. Given a one-time buyer who never returned, analyze their original purchase and generate 3 personalized product recommendations with compelling reasons to return. Write a reactivation email and an SMS nudge. Respond JSON: {subject_line: string, email_body: string, recommendation_1: string, recommendation_2: string, recommendation_3: string, sms_text: string, win_back_angle: string}",
        "Customer: {customer_email} | First order category: {preferred_category} | Order value: {first_order_total} | Product: {first_order_product} | Value segment: {value_segment} | Days lapsed: {days_lapsed} | Urgency: {urgency_level} | Offer: {reactivation_offer} (code: {offer_code})",
        [
          { field: "subject_line", as: "reactivation_subject", type: "string" },
          { field: "email_body", as: "reactivation_body", type: "string" },
          { field: "recommendation_1", as: "recommendation_1", type: "string" },
          { field: "recommendation_2", as: "recommendation_2", type: "string" },
          { field: "recommendation_3", as: "recommendation_3", type: "string" },
          { field: "sms_text", as: "sms_text", type: "string" },
          { field: "win_back_angle", as: "win_back_angle", type: "string" },
        ],
      ),

      // 4. Shopify enrichment with price/stock
      shopifyNode(
        "4",
        "Enrich recommendations with Shopify data",
        760,
        "GET",
        "products.json?ids={recommended_product_ids}&fields=id,title,variants",
        [],
        [
          {
            name: "products[0].variants[0].price",
            customName: "rec1_price",
            type: "string",
          },
          {
            name: "products[0].variants[0].inventory_quantity",
            customName: "rec1_stock",
            type: "number",
          },
          {
            name: "products[1].variants[0].price",
            customName: "rec2_price",
            type: "string",
          },
          {
            name: "products[1].variants[0].inventory_quantity",
            customName: "rec2_stock",
            type: "number",
          },
        ],
      ),

      // 5. Reactivation email
      emailNode(
        "5",
        "Send reactivation email",
        1000,
        "{customer_email}",
        "{reactivation_subject}",
        "{reactivation_body}\\n\\nRecommended for you:\\n1. {recommendation_1} - ${rec1_price}\\n2. {recommendation_2} - ${rec2_price}\\n3. {recommendation_3}\\n\\nUse code {offer_code} for {reactivation_offer}.",
      ),

      // 6. SMS for non-openers (Twilio)
      n("6", "sendSMS", "SMS reactivation nudge (Twilio)", 1240, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message: "{sms_text}",
        },
      }),

      // 7. Retention Slack alert
      slackNode(
        "7",
        "Slack: Retention team alert",
        1480,
        "One-time buyer reactivation sent:\\nCustomer: {customer_email}\\nSegment: {value_segment} | Lapsed: {days_lapsed} days\\nAngle: {win_back_angle}\\nOffer: {reactivation_offer}",
      ),

      // 8. Reactivation logging (Sheets)
      sheetsNode(
        "8",
        "Log reactivation to Sheets",
        1720,
        "OneTimeBuyerReactivation",
        '[["{customer_email}","{preferred_category}","{first_order_total}","{value_segment}","{days_lapsed}","{urgency_level}","{reactivation_offer}","{win_back_angle}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 394. FLASH SALE ANNOUNCEMENT (8 nodes)
  // ===================================================================

  "Flash Sale Announcement": {
    summary:
      "Orchestrates flash sale communications across channels. A calendar trigger verifies catalog and inventory, segments the audience, uses AI to generate urgency copy, sends VIP early-access emails, follows with general sale emails and SMS blasts, and tracks real-time revenue.",
    nodes: [
      trigger("Schedule: Flash sale trigger", "schedule", {
        schedule: "As configured per flash sale calendar",
        howItStarts:
          "Triggers based on the flash sale calendar when a sale event is scheduled to begin",
        setup:
          "Configure the flash sale schedule in your calendar. The workflow fires at the designated start time and orchestrates all sale communications.",
        inputFields: [],
      }),

      // 2. Catalog/inventory verification (runCode)
      n("2", "runCode", "Verify catalog and inventory", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify sale items are in stock and prices are correctly set
const saleItems = (vars.sale_product_ids || "").split(",");
const saleDuration = parseInt(vars.sale_duration_hours) || 24;
const discountPct = parseInt(vars.sale_discount_percent) || 20;
const result = {
  verified_product_count: saleItems.length,
  sale_end_time: new Date(Date.now() + saleDuration * 60 * 60 * 1000).toISOString(),
  discount_display: discountPct + "% OFF",
  sale_urgency: saleDuration <= 6 ? "extreme" : saleDuration <= 12 ? "high" : "moderate",
  inventory_status: "verified",
  sale_hashtag: "#FlashSale" + discountPct
};
return result;`,
          outputVariables: [
            { name: "verified_product_count", type: "number" },
            { name: "sale_end_time", type: "string" },
            { name: "discount_display", type: "string" },
            { name: "sale_urgency", type: "string" },
            { name: "inventory_status", type: "string" },
            { name: "sale_hashtag", type: "string" },
          ],
        },
      }),

      // 3. Audience segmentation
      n("3", "runCode", "Segment audience by tier", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Segment customers into VIP early-access and general audience
const result = {
  vip_segment: "vip_early_access",
  general_segment: "all_subscribers",
  vip_head_start_minutes: 30,
  total_audience_size: parseInt(vars.total_subscribers) || 0,
  vip_count: parseInt(vars.vip_count) || 0
};
return result;`,
          outputVariables: [
            { name: "vip_segment", type: "string" },
            { name: "general_segment", type: "string" },
            { name: "vip_head_start_minutes", type: "number" },
            { name: "total_audience_size", type: "number" },
            { name: "vip_count", type: "number" },
          ],
        },
      }),

      // 4. AI urgency copy
      aiNode(
        "4",
        "AI generate urgency sale copy",
        760,
        "You are a flash sale copywriter. Generate compelling, high-urgency copy for a flash sale announcement. Create a VIP early-access email, a general announcement email, and an SMS blast. Each should convey urgency proportional to the sale duration. Respond JSON: {vip_subject: string, vip_body: string, general_subject: string, general_body: string, sms_blast: string, hero_headline: string}",
        "Sale: {discount_display} | Duration: {sale_duration_hours} hours | Urgency: {sale_urgency} | Products: {verified_product_count} items | End time: {sale_end_time} | VIP head start: {vip_head_start_minutes} min | Sale name: {sale_name}",
        [
          { field: "vip_subject", as: "vip_subject", type: "string" },
          { field: "vip_body", as: "vip_body", type: "string" },
          { field: "general_subject", as: "general_subject", type: "string" },
          { field: "general_body", as: "general_body", type: "string" },
          { field: "sms_blast", as: "sms_blast", type: "string" },
          { field: "hero_headline", as: "hero_headline", type: "string" },
        ],
      ),

      // 5. VIP early-access email
      emailNode(
        "5",
        "VIP early-access email",
        1000,
        "{vip_segment}",
        "{vip_subject}",
        "{vip_body}",
      ),

      // 6. General sale email
      emailNode(
        "6",
        "General sale announcement email",
        1240,
        "{general_segment}",
        "{general_subject}",
        "{general_body}",
      ),

      // 7. SMS blast (Twilio)
      n("7", "sendSMS", "SMS flash sale blast (Twilio)", 1480, {
        sendSmsDetails: {
          to: "{sms_subscriber_list}",
          message: "{sms_blast}",
        },
      }),

      // 8. Real-time revenue logging (Sheets)
      sheetsNode(
        "8",
        "Log flash sale launch to Sheets",
        1720,
        "FlashSaleLog",
        '[["{sale_name}","{discount_display}","{sale_urgency}","{verified_product_count}","{vip_count}","{total_audience_size}","{sale_end_time}","{hero_headline}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 395. FLASH SALE CAMPAIGN LAUNCHER (10 nodes)
  // ===================================================================

  "Flash Sale Campaign Launcher": {
    summary:
      "Full-lifecycle flash sale management from teaser through post-sale analysis. Validates pricing and stock, generates an AI copy suite, sends teaser/launch/midpoint/last-chance emails with SMS at key moments, tracks real-time metrics, and produces a post-sale performance report.",
    nodes: [
      trigger("Schedule: Campaign calendar trigger", "schedule", {
        schedule: "As configured per campaign calendar",
        howItStarts:
          "Triggers from the campaign calendar at the designated pre-sale time to begin the full flash sale lifecycle",
        setup:
          "Configure your campaign calendar with sale dates, discount tiers, and product lists. The workflow orchestrates the entire sale lifecycle from teaser to post-sale report.",
        inputFields: [],
      }),

      // 2. Pricing/stock validation
      n("2", "runCode", "Validate pricing and stock levels", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate sale pricing is set and stock is sufficient
const productIds = (vars.sale_product_ids || "").split(",");
const discountPct = parseInt(vars.discount_percent) || 25;
const minStock = parseInt(vars.min_stock_threshold) || 10;
const result = {
  validated_products: productIds.length,
  discount_label: discountPct + "% OFF EVERYTHING",
  sale_start: vars.sale_start_time || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
  sale_end: vars.sale_end_time || new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString(),
  stock_status: "validated",
  pricing_status: "confirmed",
  campaign_id: "FLASH-" + Date.now().toString(36).toUpperCase()
};
return result;`,
          outputVariables: [
            { name: "validated_products", type: "number" },
            { name: "discount_label", type: "string" },
            { name: "sale_start", type: "string" },
            { name: "sale_end", type: "string" },
            { name: "stock_status", type: "string" },
            { name: "campaign_id", type: "string" },
          ],
        },
      }),

      // 3. AI full copy suite
      aiNode(
        "3",
        "AI generate full sale copy suite",
        520,
        "You are a campaign copywriter creating a complete flash sale email sequence. Generate: (1) teaser email building anticipation, (2) launch email with excitement, (3) midpoint best-seller refresh, (4) last-chance urgency email, and (5) two SMS messages for launch and final hour. Respond JSON: {teaser_subject: string, teaser_body: string, launch_subject: string, launch_body: string, midpoint_subject: string, midpoint_body: string, lastchance_subject: string, lastchance_body: string, sms_launch: string, sms_final: string}",
        "Sale name: {sale_name} | Discount: {discount_label} | Products: {validated_products} | Start: {sale_start} | End: {sale_end} | Campaign: {campaign_id} | Brand voice: {brand_voice}",
        [
          { field: "teaser_subject", as: "teaser_subject", type: "string" },
          { field: "teaser_body", as: "teaser_body", type: "string" },
          { field: "launch_subject", as: "launch_subject", type: "string" },
          { field: "launch_body", as: "launch_body", type: "string" },
          { field: "midpoint_subject", as: "midpoint_subject", type: "string" },
          { field: "midpoint_body", as: "midpoint_body", type: "string" },
          {
            field: "lastchance_subject",
            as: "lastchance_subject",
            type: "string",
          },
          { field: "lastchance_body", as: "lastchance_body", type: "string" },
          { field: "sms_launch", as: "sms_launch", type: "string" },
          { field: "sms_final", as: "sms_final", type: "string" },
        ],
      ),

      // 4. Teaser email
      emailNode(
        "4",
        "Send teaser email",
        760,
        "{subscriber_list}",
        "{teaser_subject}",
        "{teaser_body}",
      ),

      // 5. Launch email
      emailNode(
        "5",
        "Send launch email",
        1000,
        "{subscriber_list}",
        "{launch_subject}",
        "{launch_body}",
      ),

      // 6. Midpoint best-seller refresh email
      emailNode(
        "6",
        "Send midpoint best-seller refresh",
        1240,
        "{subscriber_list}",
        "{midpoint_subject}",
        "{midpoint_body}",
      ),

      // 7. Last-chance email
      emailNode(
        "7",
        "Send last-chance email",
        1480,
        "{subscriber_list}",
        "{lastchance_subject}",
        "{lastchance_body}",
      ),

      // 8. SMS at launch and final hour (Twilio)
      n("8", "sendSMS", "SMS launch + final hour (Twilio)", 1720, {
        sendSmsDetails: {
          to: "{sms_subscriber_list}",
          message: "{sms_launch}",
        },
      }),

      // 9. Real-time metrics tracking
      n("9", "runCode", "Track real-time sale metrics", 1960, {
        runCodeDetails: {
          language: "javascript",
          code: `// Compile real-time metrics for the flash sale
const result = {
  campaign_id: vars.campaign_id || "unknown",
  emails_sent_total: 4,
  sms_sent: "yes",
  sequence_phases: "teaser,launch,midpoint,lastchance",
  completion_status: "all_phases_sent",
  timestamp: new Date().toISOString()
};
return result;`,
          outputVariables: [
            { name: "campaign_id", type: "string" },
            { name: "emails_sent_total", type: "number" },
            { name: "sequence_phases", type: "string" },
            { name: "completion_status", type: "string" },
          ],
        },
      }),

      // 10. Post-sale performance report logging
      sheetsNode(
        "10",
        "Log post-sale performance to Sheets",
        2200,
        "FlashSalePerformance",
        '[["{campaign_id}","{sale_name}","{discount_label}","{validated_products}","{sale_start}","{sale_end}","{emails_sent_total}","{completion_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
      e("9", "10"),
    ],
  },

  // ===================================================================
  // 396. FREE-SHIPPING RECOVERY (8 nodes)
  // ===================================================================

  "Free-Shipping Recovery": {
    summary:
      "Recovers abandoned carts by leveraging free-shipping threshold proximity. Calculates how close the cart is to the free-shipping threshold, routes by qualifying status, sends threshold-aware emails with add-on upsells or alternative incentives, generates discount codes when needed, and logs path conversion data.",
    nodes: [
      trigger("Webhook: Cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "cart.abandoned",
        howItStarts:
          "A cart is abandoned and the customer is near or above the free-shipping threshold",
        setup:
          "Connect your Shopify store or e-commerce platform to POST cart abandonment data (cart total, items, free-shipping threshold, customer email) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Threshold proximity calculation (runCode)
      n("2", "runCode", "Calculate free-shipping proximity", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate how close the cart is to the free-shipping threshold
const cartTotal = parseFloat(vars.cart_total) || 0;
const threshold = parseFloat(vars.free_shipping_threshold) || 75;
const gap = threshold - cartTotal;
const result = {
  shipping_gap: Math.max(0, gap).toFixed(2),
  gap_percentage: ((gap / threshold) * 100).toFixed(1),
  qualifies_for_free_shipping: cartTotal >= threshold ? "yes" : "no",
  near_threshold: gap > 0 && gap <= 25 ? "yes" : "no",
  gap_tier: gap <= 0 ? "qualified" : gap <= 10 ? "very_close" : gap <= 25 ? "close" : "far",
  suggested_addon_price: gap > 0 ? Math.ceil(gap) : 0,
  threshold_display: "$" + threshold.toFixed(2)
};
return result;`,
          outputVariables: [
            { name: "shipping_gap", type: "string" },
            { name: "gap_percentage", type: "string" },
            { name: "qualifies_for_free_shipping", type: "string" },
            { name: "near_threshold", type: "string" },
            { name: "gap_tier", type: "string" },
            { name: "suggested_addon_price", type: "number" },
            { name: "threshold_display", type: "string" },
          ],
        },
      }),

      // 3. Qualifying condition
      n("3", "condition", "Near free-shipping threshold?", 520, {
        conditionData: [
          {
            variable: "near_threshold",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. "Don't lose free shipping" email
      emailNode(
        "4",
        "Email: Don't lose free shipping!",
        760,
        "{customer_email}",
        "You're just ${shipping_gap} away from FREE shipping!",
        "Hi {customer_name},\\n\\nYour cart is so close to free shipping! You are only ${shipping_gap} away from our {threshold_display} free-shipping threshold.\\n\\nComplete your order and add a little something extra to save on shipping entirely.\\n\\nYour cart: {cart_items}\\nCurrent total: ${cart_total}\\nFree shipping at: {threshold_display}\\n\\nReturn to your cart: {cart_recovery_link}",
      ),

      // 5. Near-threshold add-on upsell
      emailNode(
        "5",
        "Email: Add-on upsell suggestions",
        1000,
        "{customer_email}",
        "Add just one more item for FREE shipping",
        "Here are some popular items under ${suggested_addon_price} that would qualify your cart for free shipping:\\n\\n{addon_suggestions}\\n\\nAdd one to your cart and ship for free: {cart_recovery_link}",
      ),

      // 6. Below-threshold incentive path (far from threshold)
      emailNode(
        "6",
        "Email: Flat-rate shipping incentive",
        1240,
        "{customer_email}",
        "We will cover part of your shipping — complete your order!",
        "Hi {customer_name},\\n\\nWe noticed you left some great items in your cart. While your total is below our free-shipping threshold, we would like to offer you discounted flat-rate shipping.\\n\\nUse code {shipping_discount_code} for $3.99 flat-rate shipping on your order.\\n\\nReturn to your cart: {cart_recovery_link}",
        460,
      ),

      // 7. Code generation if needed
      n("7", "runCode", "Generate shipping discount code", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Generate a shipping discount code for below-threshold carts
const gapTier = vars.gap_tier || "far";
const result = {
  shipping_discount_code: "SHIP" + Date.now().toString(36).toUpperCase().slice(-5),
  shipping_discount_type: gapTier === "far" ? "flat_rate_399" : "free_shipping",
  recovery_path: gapTier === "qualified" || gapTier === "very_close" ? "upsell_to_threshold" : "discount_shipping",
  discount_expiry: new Date(Date.now() + 48 * 60 * 60 * 1000).toISOString().split("T")[0]
};
return result;`,
          outputVariables: [
            { name: "shipping_discount_code", type: "string" },
            { name: "shipping_discount_type", type: "string" },
            { name: "recovery_path", type: "string" },
            { name: "discount_expiry", type: "string" },
          ],
        },
      }),

      // 8. Path conversion logging (Sheets)
      sheetsNode(
        "8",
        "Log shipping recovery path to Sheets",
        1720,
        "FreeShippingRecovery",
        '[["{customer_email}","{cart_total}","{shipping_gap}","{gap_tier}","{recovery_path}","{shipping_discount_code}","{shipping_discount_type}","{threshold_display}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // near threshold -> "don't lose it" email
      e("3", "7", "output_2"), // far from threshold -> generate code first
      e("4", "5"),
      e("5", "8"),
      e("7", "6"),
      e("6", "8"),
    ],
  },

  // ===================================================================
  // 397. GIFT CARD BALANCE CHECKER BOT (7 nodes)
  // ===================================================================

  "Gift Card Balance Checker Bot": {
    summary:
      "Conversational bot that checks gift card balances via chat or SMS. Validates the gift card against Shopify, generates a friendly AI response, routes by channel, sends an email summary or SMS quick balance, and logs all inquiries.",
    nodes: [
      trigger("Webhook: Chat/SMS balance inquiry", "webhook_app", {
        source: "Chat Widget / SMS (Twilio)",
        event: "giftcard.balance.inquiry",
        howItStarts:
          "A customer asks about their gift card balance via chat widget or SMS",
        setup:
          "Connect your chat widget or Twilio SMS number to POST gift card balance inquiries (message text, channel, customer contact info) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Gift card validation (Shopify)
      shopifyNode(
        "2",
        "Validate gift card on Shopify",
        280,
        "GET",
        "gift_cards/{gift_card_id}.json",
        [],
        [
          {
            name: "gift_card.balance",
            customName: "card_balance",
            type: "string",
          },
          {
            name: "gift_card.currency",
            customName: "card_currency",
            type: "string",
          },
          {
            name: "gift_card.expires_on",
            customName: "card_expiry",
            type: "string",
          },
          {
            name: "gift_card.disabled_at",
            customName: "card_disabled",
            type: "string",
          },
          {
            name: "gift_card.last_characters",
            customName: "card_last_chars",
            type: "string",
          },
        ],
      ),

      // 3. AI conversational response
      aiNode(
        "3",
        "AI generate conversational balance response",
        520,
        "You are a friendly gift card assistant. Given the gift card balance, expiry, and status, generate a warm, helpful response. If the card is disabled or expired, explain gently and offer next steps. Include the balance prominently. Adapt tone for the channel (chat = casual, email = formal). Respond JSON: {chat_response: string, email_summary: string, sms_balance: string, card_status: string, needs_support: string}",
        "Balance: {card_balance} {card_currency} | Expires: {card_expiry} | Disabled: {card_disabled} | Last chars: {card_last_chars} | Channel: {inquiry_channel} | Customer: {customer_name}",
        [
          { field: "chat_response", as: "chat_response", type: "string" },
          { field: "email_summary", as: "email_summary", type: "string" },
          { field: "sms_balance", as: "sms_balance", type: "string" },
          { field: "card_status", as: "card_status", type: "string" },
          { field: "needs_support", as: "needs_support", type: "string" },
        ],
      ),

      // 4. Channel routing condition
      n("4", "condition", "Inquiry via email channel?", 760, {
        conditionData: [
          {
            variable: "inquiry_channel",
            value: "email",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Email summary
      emailNode(
        "5",
        "Email gift card balance summary",
        1000,
        "{customer_email}",
        "Your gift card balance: {card_balance} {card_currency}",
        "{email_summary}",
      ),

      // 6. SMS quick balance (Twilio)
      n(
        "6",
        "sendSMS",
        "SMS quick balance reply (Twilio)",
        1000,
        {
          sendSmsDetails: {
            to: "{customer_phone}",
            message: "{sms_balance}",
          },
        },
        460,
      ),

      // 7. Inquiry logging (Sheets)
      sheetsNode(
        "7",
        "Log balance inquiry to Sheets",
        1240,
        "GiftCardInquiries",
        '[["{customer_name}","{inquiry_channel}","{card_last_chars}","{card_balance}","{card_currency}","{card_expiry}","{card_status}","{needs_support}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // email channel -> email summary
      e("4", "6", "output_2"), // SMS/chat channel -> SMS reply
      e("5", "7"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 398. GIFT CART ABANDONMENT (9 nodes)
  // ===================================================================

  "Gift Cart Abandonment": {
    summary:
      "Recovers abandoned gift carts with occasion-aware urgency. Detects gift signals, infers the occasion and delivery deadline via AI, calculates urgency timing, sends occasion-tailored recovery emails with gift presentation upsells, follows up with SMS deadline reminders, offers faster shipping when applicable, and logs gift recovery metrics.",
    nodes: [
      trigger("Webhook: Gift cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "cart.abandoned",
        howItStarts:
          "A cart containing gift-wrapped items, gift cards, or items flagged as gifts is abandoned",
        setup:
          "Connect your Shopify store or e-commerce platform to POST gift cart abandonment data (cart items, gift wrap flag, gift message, customer email, cart total) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Gift signal detection (runCode)
      n("2", "runCode", "Detect gift signals in cart", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect gift indicators in the abandoned cart
const hasGiftWrap = vars.gift_wrap_flag === "true" || vars.gift_wrap_flag === true;
const hasGiftMessage = (vars.gift_message || "").length > 0;
const hasGiftCard = (vars.cart_items || "").toLowerCase().includes("gift card");
const giftConfidence = (hasGiftWrap ? 40 : 0) + (hasGiftMessage ? 40 : 0) + (hasGiftCard ? 20 : 0);
const result = {
  is_gift_purchase: giftConfidence >= 40 ? "yes" : "no",
  gift_confidence_score: giftConfidence,
  gift_signals: [hasGiftWrap ? "gift_wrap" : null, hasGiftMessage ? "gift_message" : null, hasGiftCard ? "gift_card" : null].filter(Boolean).join(", "),
  gift_message_text: vars.gift_message || "",
  has_delivery_deadline: hasGiftMessage ? "likely" : "unknown"
};
return result;`,
          outputVariables: [
            { name: "is_gift_purchase", type: "string" },
            { name: "gift_confidence_score", type: "number" },
            { name: "gift_signals", type: "string" },
            { name: "gift_message_text", type: "string" },
            { name: "has_delivery_deadline", type: "string" },
          ],
        },
      }),

      // 3. AI occasion inference
      aiNode(
        "3",
        "AI infer occasion and urgency",
        520,
        "You are a gift commerce specialist. Analyze the cart items, gift message, and purchase timing to infer the likely occasion (birthday, holiday, anniversary, etc.) and delivery deadline urgency. Generate occasion-appropriate recovery copy with urgency calibrated to the deadline. Respond JSON: {occasion: string, estimated_deadline: string, urgency_level: string, recovery_subject: string, recovery_body: string, gift_upsell: string, sms_reminder: string, deadline_display: string}",
        "Cart items: {cart_items} | Cart total: {cart_total} | Gift message: {gift_message_text} | Gift signals: {gift_signals} | Customer: {customer_email} | Current date: {current_date}",
        [
          { field: "occasion", as: "occasion", type: "string" },
          {
            field: "estimated_deadline",
            as: "estimated_deadline",
            type: "string",
          },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          { field: "recovery_subject", as: "recovery_subject", type: "string" },
          { field: "recovery_body", as: "recovery_body", type: "string" },
          { field: "gift_upsell", as: "gift_upsell", type: "string" },
          { field: "sms_reminder", as: "sms_reminder", type: "string" },
          { field: "deadline_display", as: "deadline_display", type: "string" },
        ],
      ),

      // 4. Delivery deadline calculation
      n("4", "runCode", "Calculate delivery deadline feasibility", 760, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate if standard or expedited shipping can meet the deadline
const deadline = new Date(vars.estimated_deadline || Date.now() + 14 * 86400000);
const now = new Date();
const daysUntilDeadline = Math.ceil((deadline - now) / 86400000);
const result = {
  days_until_deadline: daysUntilDeadline,
  standard_shipping_feasible: daysUntilDeadline >= 7 ? "yes" : "no",
  express_shipping_needed: daysUntilDeadline < 7 && daysUntilDeadline >= 3 ? "yes" : "no",
  overnight_needed: daysUntilDeadline < 3 ? "yes" : "no",
  shipping_recommendation: daysUntilDeadline >= 7 ? "standard" : daysUntilDeadline >= 3 ? "express" : "overnight",
  deadline_urgency_copy: daysUntilDeadline <= 2 ? "Order TODAY to arrive in time!" : daysUntilDeadline <= 5 ? "Order soon to ensure delivery!" : "Plenty of time with standard shipping"
};
return result;`,
          outputVariables: [
            { name: "days_until_deadline", type: "number" },
            { name: "standard_shipping_feasible", type: "string" },
            { name: "express_shipping_needed", type: "string" },
            { name: "shipping_recommendation", type: "string" },
            { name: "deadline_urgency_copy", type: "string" },
          ],
        },
      }),

      // 5. Occasion-aware urgency email
      emailNode(
        "5",
        "Occasion-aware gift recovery email",
        1000,
        "{customer_email}",
        "{recovery_subject}",
        "{recovery_body}\\n\\n{deadline_urgency_copy}\\n\\nDeadline: {deadline_display} | Recommended shipping: {shipping_recommendation}",
      ),

      // 6. Gift presentation upsell email
      emailNode(
        "6",
        "Gift presentation upsell email",
        1240,
        "{customer_email}",
        "Make it extra special — premium gift wrapping available",
        "{gift_upsell}\\n\\nComplete your gift: {cart_recovery_link}",
      ),

      // 7. SMS deadline reminder (Twilio)
      n("7", "sendSMS", "SMS gift deadline reminder (Twilio)", 1480, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message: "{sms_reminder}",
        },
      }),

      // 8. Faster-shipping alternatives condition
      n("8", "condition", "Express shipping needed?", 1720, {
        conditionData: [
          {
            variable: "express_shipping_needed",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 9. Gift recovery logging (Sheets)
      sheetsNode(
        "9",
        "Log gift recovery to Sheets",
        1960,
        "GiftCartRecovery",
        '[["{customer_email}","{occasion}","{cart_total}","{gift_signals}","{urgency_level}","{days_until_deadline}","{shipping_recommendation}","{gift_confidence_score}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9", "output_1"), // express needed -> log with shipping flag
      e("8", "9", "output_2"), // standard ok -> log normally
    ],
  },

  // ===================================================================
  // 399. GIFT GUIDE BOT (8 nodes)
  // ===================================================================

  "Gift Guide Bot": {
    summary:
      "Conversational gift guide that helps customers find the perfect gift. Collects recipient, occasion, and budget preferences via chat, searches the Shopify catalog, uses AI to rank and suggest gifts with bundle options, creates a one-click cart, escalates to a human when needed, and logs gift conversion data.",
    nodes: [
      trigger("Webhook: Chat gift guide request", "webhook_app", {
        source: "Chat Widget / Website",
        event: "chat.gift_guide.started",
        howItStarts:
          "A customer initiates a gift guide conversation through the chat widget or gift guide page",
        setup:
          "Connect your chat widget or gift guide landing page to POST gift guide requests (customer message, session info) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Recipient/occasion/budget collection (AI extraction)
      aiNode(
        "2",
        "AI extract gift preferences",
        280,
        "You are a gift concierge. Extract the recipient details (who the gift is for, their relationship, age range), the occasion (birthday, holiday, anniversary, etc.), and the budget range from the customer message. If any are missing, note what to ask. Respond JSON: {recipient_type: string, recipient_age_range: string, occasion: string, budget_min: number, budget_max: number, interests: string, missing_info: string, search_query: string}",
        "{webhook_payload}",
        [
          { field: "recipient_type", as: "recipient_type", type: "string" },
          {
            field: "recipient_age_range",
            as: "recipient_age_range",
            type: "string",
          },
          { field: "occasion", as: "occasion", type: "string" },
          { field: "budget_min", as: "budget_min", type: "number" },
          { field: "budget_max", as: "budget_max", type: "number" },
          { field: "interests", as: "interests", type: "string" },
          { field: "search_query", as: "search_query", type: "string" },
        ],
      ),

      // 3. Catalog search (Shopify)
      shopifyNode(
        "3",
        "Search Shopify catalog for gifts",
        520,
        "GET",
        "products.json?title={search_query}&limit=10",
        [],
        [
          {
            name: "products[0].title",
            customName: "gift_1_title",
            type: "string",
          },
          {
            name: "products[0].variants[0].price",
            customName: "gift_1_price",
            type: "string",
          },
          { name: "products[0].id", customName: "gift_1_id", type: "string" },
          {
            name: "products[1].title",
            customName: "gift_2_title",
            type: "string",
          },
          {
            name: "products[1].variants[0].price",
            customName: "gift_2_price",
            type: "string",
          },
          {
            name: "products[2].title",
            customName: "gift_3_title",
            type: "string",
          },
          {
            name: "products[2].variants[0].price",
            customName: "gift_3_price",
            type: "string",
          },
        ],
      ),

      // 4. AI gift ranking + bundle suggestion
      aiNode(
        "4",
        "AI rank gifts and suggest bundles",
        760,
        "You are a gift recommendation expert. Given the search results and customer preferences, rank the gifts by relevance, suggest a gift bundle combination, and write a conversational response recommending the top picks. Include why each gift is a great fit. Respond JSON: {top_pick: string, top_pick_reason: string, bundle_suggestion: string, bundle_total: string, chat_response: string, gift_wrap_note: string}",
        "Recipient: {recipient_type} (age: {recipient_age_range}) | Occasion: {occasion} | Budget: ${budget_min}-${budget_max} | Interests: {interests} | Gift 1: {gift_1_title} (${gift_1_price}) | Gift 2: {gift_2_title} (${gift_2_price}) | Gift 3: {gift_3_title} (${gift_3_price})",
        [
          { field: "top_pick", as: "top_pick", type: "string" },
          { field: "top_pick_reason", as: "top_pick_reason", type: "string" },
          {
            field: "bundle_suggestion",
            as: "bundle_suggestion",
            type: "string",
          },
          { field: "bundle_total", as: "bundle_total", type: "string" },
          { field: "chat_response", as: "chat_response", type: "string" },
          { field: "gift_wrap_note", as: "gift_wrap_note", type: "string" },
        ],
      ),

      // 5. Chat response with gift suggestions
      n("5", "sendMessage", "Deliver gift suggestions to chat", 1000, {
        sendMessageDetails: {
          conversationId: "{conversation_id}",
          message:
            "{chat_response}\n\nBundle idea: {bundle_suggestion} ({bundle_total})\n\n{gift_wrap_note}",
        },
      }),

      // 6. One-click cart creation
      shopifyNode(
        "6",
        "Create one-click gift cart",
        1240,
        "POST",
        "checkouts.json",
        [
          {
            key: "checkout",
            value:
              '{"line_items":[{"variant_id":"{gift_1_id}","quantity":1}],"note":"Gift for {recipient_type} - {occasion}"}',
          },
        ],
        [
          {
            name: "checkout.web_url",
            customName: "checkout_url",
            type: "string",
          },
          {
            name: "checkout.token",
            customName: "checkout_token",
            type: "string",
          },
        ],
      ),

      // 7. Human escalation condition
      n("7", "condition", "Needs human gift concierge?", 1480, {
        conditionData: [
          {
            variable: "budget_max",
            value: "200",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Gift conversion logging (Sheets)
      sheetsNode(
        "8",
        "Log gift guide conversion to Sheets",
        1720,
        "GiftGuideConversions",
        '[["{customer_email}","{recipient_type}","{occasion}","{budget_min}","{budget_max}","{top_pick}","{bundle_total}","{checkout_url}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"), // high budget -> log + flag for concierge
      e("7", "8", "output_2"), // standard budget -> log normally
    ],
  },

  // ===================================================================
  // 400. GIFT RECIPIENT ONBOARDING (8 nodes)
  // ===================================================================

  "Gift Recipient Onboarding": {
    summary:
      "Converts gift recipients into customers. On gift redemption, checks if the recipient has an account, sends an account invitation, delivers a personalized AI welcome for the gifted item, provides care and setup content, offers a first self-purchase discount, enrolls in HubSpot CRM, and tracks recipient-to-customer conversion.",
    nodes: [
      trigger("Webhook: Gift redeemed", "webhook_app", {
        source: "Shopify / Gift Platform",
        event: "gift.redeemed",
        howItStarts:
          "A gift recipient redeems their gift card or opens their gifted product for the first time",
        setup:
          "Connect your Shopify store or gift platform to POST gift redemption data (recipient email, gift product, gifter name, gift message, redemption method) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Account check condition
      n("2", "condition", "Recipient has existing account?", 280, {
        conditionData: [
          {
            variable: "has_account",
            value: "true",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3. Account invitation email
      emailNode(
        "3",
        "Account invitation for gift recipient",
        520,
        "{recipient_email}",
        "Someone special sent you a gift — create your account!",
        'Hi there!\\n\\n{gifter_name} sent you a wonderful gift: {gift_product_name}.\\n\\nCreate your account to:\\n- Track your gift and its care instructions\\n- Get exclusive offers as a new member\\n- Save your preferences for future shopping\\n\\nCreate your account: {account_creation_link}\\n\\nGift message from {gifter_name}: \\"{gift_message}\\"',
      ),

      // 4. AI welcome for gifted item
      aiNode(
        "4",
        "AI personalized gift welcome",
        760,
        "You are a warm, enthusiastic brand ambassador welcoming a gift recipient. Create a personalized welcome message that celebrates the gift, introduces the brand, highlights the specific product they received, and gently encourages them to explore more. Respond JSON: {welcome_subject: string, welcome_body: string, product_tips: string, care_instructions: string, first_purchase_angle: string}",
        "Recipient: {recipient_email} | Gift product: {gift_product_name} | Category: {gift_product_category} | Gifter: {gifter_name} | Gift message: {gift_message} | Product value: {gift_product_value}",
        [
          { field: "welcome_subject", as: "welcome_subject", type: "string" },
          { field: "welcome_body", as: "welcome_body", type: "string" },
          { field: "product_tips", as: "product_tips", type: "string" },
          {
            field: "care_instructions",
            as: "care_instructions",
            type: "string",
          },
          {
            field: "first_purchase_angle",
            as: "first_purchase_angle",
            type: "string",
          },
        ],
      ),

      // 5. Care/setup content email
      emailNode(
        "5",
        "Send care and setup content",
        1000,
        "{recipient_email}",
        "{welcome_subject}",
        "{welcome_body}\\n\\nProduct tips: {product_tips}\\n\\nCare instructions: {care_instructions}",
      ),

      // 6. First self-purchase offer
      emailNode(
        "6",
        "First self-purchase offer",
        1240,
        "{recipient_email}",
        "Loved your gift? Here is 20% off your first purchase",
        "{first_purchase_angle}\\n\\nAs a gift recipient, enjoy 20% off your first self-purchase with code GIFTED20.\\n\\nShop now: {shop_link}",
      ),

      // 7. CRM enrollment (HubSpot)
      n("7", "hitApi", "Enroll gift recipient in HubSpot", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{recipient_email}","acquisition_source":"gift_recipient","gifter_name":"{gifter_name}","gift_product":"{gift_product_name}","lifecyclestage":"lead","first_purchase_offer":"GIFTED20"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      // 8. Recipient-to-customer logging (Sheets)
      sheetsNode(
        "8",
        "Log gift recipient onboarding to Sheets",
        1720,
        "GiftRecipientOnboarding",
        '[["{recipient_email}","{gifter_name}","{gift_product_name}","{gift_product_category}","{gift_product_value}","{has_account}","{hubspot_contact_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "4", "output_1"), // has account -> skip account creation, go to welcome
      e("2", "3", "output_2"), // no account -> send invitation
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 401. GOOGLE MERCHANT FULL PRODUCT SYNC (8 nodes)
  // ===================================================================

  "Google Merchant Full Product Sync": {
    summary:
      "Synchronizes the full Shopify catalog to Google Merchant Center. A scheduled job exports the catalog, normalizes products for the Merchant API, maps categories, validates data, publishes in batch, surfaces errors on Slack, and logs every sync cycle.",
    nodes: [
      trigger("Schedule: Full catalog sync", "schedule", {
        schedule: "Daily at 02:00 UTC",
        howItStarts:
          "Runs daily to synchronize the complete Shopify product catalog to Google Merchant Center",
        setup:
          "Enable this scheduled workflow and configure your Shopify store credentials, Google Merchant Center ID, and Google API token.",
        inputFields: [],
      }),

      // 2. Shopify catalog export
      shopifyNode(
        "2",
        "Export Shopify catalog",
        280,
        "GET",
        "products.json?limit=250&fields=id,title,body_html,vendor,product_type,variants,images,tags",
        [],
        [
          {
            name: "products.length",
            customName: "total_products",
            type: "number",
          },
          { name: "products", customName: "product_catalog", type: "string" },
        ],
      ),

      // 3. Merchant API normalization (runCode)
      n("3", "runCode", "Normalize products for Merchant API", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Transform Shopify products to Google Merchant Center format
const products = JSON.parse(vars.product_catalog || "[]");
const merchantEntries = [];
let errorCount = 0;
for (const p of products) {
  const variant = (p.variants || [])[0] || {};
  const image = (p.images || [])[0] || {};
  if (!variant.price || !p.title) { errorCount++; continue; }
  merchantEntries.push({
    offerId: String(p.id),
    title: p.title.substring(0, 150),
    description: (p.body_html || "").replace(/<[^>]*>/g, "").substring(0, 5000),
    link: "https://" + (vars.shopify_store || "store") + ".myshopify.com/products/" + (p.handle || p.id),
    imageLink: image.src || "",
    price: { value: variant.price, currency: vars.store_currency || "USD" },
    availability: variant.inventory_quantity > 0 ? "in_stock" : "out_of_stock",
    brand: p.vendor || "",
    condition: "new",
    googleProductCategory: "",
    productType: p.product_type || ""
  });
}
const result = {
  merchant_entries_json: JSON.stringify(merchantEntries),
  normalized_count: merchantEntries.length,
  normalization_errors: errorCount,
  needs_category_mapping: "yes"
};
return result;`,
          outputVariables: [
            { name: "merchant_entries_json", type: "string" },
            { name: "normalized_count", type: "number" },
            { name: "normalization_errors", type: "number" },
            { name: "needs_category_mapping", type: "string" },
          ],
        },
      }),

      // 4. Category mapping (AI)
      aiNode(
        "4",
        "AI map Google product categories",
        760,
        "You are a Google Merchant Center taxonomy expert. Given product types and titles from a Shopify catalog, map each to the correct Google Product Category ID. Use the official Google taxonomy. Return a JSON mapping object. Respond JSON: {category_mappings: string, unmapped_count: number, mapping_confidence: string, taxonomy_version: string}",
        "Product types found: {product_types_list} | Store category: {store_category} | Sample titles: {sample_titles} | Total products: {normalized_count}",
        [
          {
            field: "category_mappings",
            as: "category_mappings",
            type: "string",
          },
          { field: "unmapped_count", as: "unmapped_count", type: "number" },
          {
            field: "mapping_confidence",
            as: "mapping_confidence",
            type: "string",
          },
          { field: "taxonomy_version", as: "taxonomy_version", type: "string" },
        ],
      ),

      // 5. Validation condition
      n("5", "condition", "All products valid for submission?", 1000, {
        conditionData: [
          {
            variable: "normalization_errors",
            value: "0",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6. Merchant API batch publish
      n("6", "hitApi", "Batch publish to Google Merchant Center", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products/batch",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "entries", value: "{merchant_entries_json}" }],
          variables: [
            { name: "kind", customName: "response_kind", type: "string" },
            {
              name: "entries.length",
              customName: "published_count",
              type: "number",
            },
          ],
        },
      }),

      // 7. Error surfacing (Slack)
      slackNode(
        "7",
        "Slack: Surface sync errors",
        1480,
        "Google Merchant Full Sync Complete\\nProducts exported: {total_products}\\nNormalized: {normalized_count}\\nPublished: {published_count}\\nErrors: {normalization_errors}\\nUnmapped categories: {unmapped_count}\\nMapping confidence: {mapping_confidence}",
      ),

      // 8. Sync logging (Sheets)
      sheetsNode(
        "8",
        "Log sync cycle to Sheets",
        1720,
        "MerchantSyncLog",
        '[["{total_products}","{normalized_count}","{published_count}","{normalization_errors}","{unmapped_count}","{mapping_confidence}","{taxonomy_version}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"), // all valid -> publish
      e("5", "7", "output_2"), // errors -> alert first, then publish partial
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 402. GOOGLE MERCHANT PRICE/STOCK UPDATE (8 nodes)
  // ===================================================================

  "Google Merchant Price/Stock Update": {
    summary:
      "Keeps Google Merchant Center product pricing and inventory in real time. Listens for Shopify price and inventory webhooks, batches updates in a time window, builds Merchant API patch payloads, submits batch updates, verifies acceptance, retries transient failures, alerts marketing on Slack, and logs every batch.",
    nodes: [
      trigger("Webhook: Shopify price/inventory change", "webhook_app", {
        source: "Shopify",
        event: "products/update",
        howItStarts:
          "A product price or inventory level changes in Shopify, triggering a Merchant Center sync",
        setup:
          "Connect your Shopify store products/update webhook to POST price and inventory change data to the workflow URL.",
        inputFields: [],
      }),

      // 2. Batch window (runCode)
      n("2", "runCode", "Batch updates in time window", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Collect and batch price/stock updates
const productId = vars.product_id || "";
const variantId = vars.variant_id || "";
const newPrice = vars.new_price || "0";
const newStock = parseInt(vars.new_inventory_quantity) || 0;
const result = {
  offer_id: productId,
  batch_entry: JSON.stringify({
    batchId: Date.now(),
    merchantId: vars.merchant_id || "",
    method: "update",
    productId: "online:en:US:" + productId,
    product: {
      price: { value: newPrice, currency: vars.store_currency || "USD" },
      availability: newStock > 0 ? "in_stock" : "out_of_stock"
    }
  }),
  update_type: vars.new_price !== vars.old_price ? "price_change" : "stock_change",
  old_price: vars.old_price || "unknown",
  new_price_display: "$" + newPrice,
  stock_status: newStock > 0 ? "in_stock" : "out_of_stock"
};
return result;`,
          outputVariables: [
            { name: "offer_id", type: "string" },
            { name: "batch_entry", type: "string" },
            { name: "update_type", type: "string" },
            { name: "old_price", type: "string" },
            { name: "new_price_display", type: "string" },
            { name: "stock_status", type: "string" },
          ],
        },
      }),

      // 3. Merchant API patch payload
      n("3", "runCode", "Build Merchant API patch payload", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Build the final batch payload for Merchant Center
const entry = JSON.parse(vars.batch_entry || "{}");
const result = {
  patch_payload: JSON.stringify({ entries: [entry] }),
  product_resource: "online:en:US:" + vars.offer_id,
  update_fields: vars.update_type === "price_change" ? "price,availability" : "availability"
};
return result;`,
          outputVariables: [
            { name: "patch_payload", type: "string" },
            { name: "product_resource", type: "string" },
            { name: "update_fields", type: "string" },
          ],
        },
      }),

      // 4. Batch update submission
      n("4", "hitApi", "Submit batch update to Merchant Center", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products/batch",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "entries", value: "{patch_payload}" }],
          variables: [
            { name: "kind", customName: "batch_response_kind", type: "string" },
            {
              name: "entries[0].errors",
              customName: "batch_errors",
              type: "string",
            },
            {
              name: "entries[0].batchId",
              customName: "batch_id",
              type: "number",
            },
          ],
        },
      }),

      // 5. Acceptance verification
      n("5", "runCode", "Verify update acceptance", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if the batch update was accepted
const errors = vars.batch_errors || "";
const hasErrors = errors && errors !== "null" && errors !== "undefined" && errors.length > 2;
const result = {
  update_accepted: hasErrors ? "no" : "yes",
  error_details: hasErrors ? errors : "none",
  is_transient_error: hasErrors && (errors.includes("rate") || errors.includes("timeout") || errors.includes("503")) ? "yes" : "no",
  retry_recommended: hasErrors && (errors.includes("rate") || errors.includes("timeout")) ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "update_accepted", type: "string" },
            { name: "error_details", type: "string" },
            { name: "is_transient_error", type: "string" },
            { name: "retry_recommended", type: "string" },
          ],
        },
      }),

      // 6. Transient retry condition
      n("6", "condition", "Transient error — retry?", 1240, {
        conditionData: [
          {
            variable: "is_transient_error",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Marketing Slack alert
      slackNode(
        "7",
        "Slack: Marketing price/stock update alert",
        1480,
        "Merchant Center Update:\\nProduct: {offer_id}\\nType: {update_type}\\nPrice: {old_price} -> {new_price_display}\\nStock: {stock_status}\\nAccepted: {update_accepted}\\nErrors: {error_details}",
      ),

      // 8. Batch logging (Sheets)
      sheetsNode(
        "8",
        "Log batch update to Sheets",
        1720,
        "MerchantPriceStockLog",
        '[["{offer_id}","{update_type}","{old_price}","{new_price_display}","{stock_status}","{update_accepted}","{error_details}","{batch_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "4", "output_1"), // transient error -> retry submission
      e("6", "7", "output_2"), // no transient error -> alert
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 403. GOOGLE MERCHANT PRODUCT SYNC (8 nodes)
  // ===================================================================

  "Google Merchant Product Sync": {
    summary:
      "Syncs individual Shopify product changes to Google Merchant Center. Listens for product create/update webhooks, hydrates product data, uses AI for taxonomy suggestion, validates attributes, inserts or patches the Merchant API, monitors disapprovals, notifies the product team on Slack, and logs every sync.",
    nodes: [
      trigger("Webhook: Product create/update", "webhook_app", {
        source: "Shopify",
        event: "products/create | products/update",
        howItStarts:
          "A product is created or updated in Shopify, triggering an individual sync to Google Merchant Center",
        setup:
          "Connect your Shopify store products/create and products/update webhooks to POST product data to the workflow URL.",
        inputFields: [],
      }),

      // 2. Product hydration
      shopifyNode(
        "2",
        "Hydrate full product data from Shopify",
        280,
        "GET",
        "products/{product_id}.json",
        [],
        [
          {
            name: "product.title",
            customName: "product_title",
            type: "string",
          },
          {
            name: "product.body_html",
            customName: "product_description",
            type: "string",
          },
          {
            name: "product.vendor",
            customName: "product_brand",
            type: "string",
          },
          {
            name: "product.product_type",
            customName: "product_type",
            type: "string",
          },
          {
            name: "product.variants[0].price",
            customName: "product_price",
            type: "string",
          },
          {
            name: "product.variants[0].sku",
            customName: "product_sku",
            type: "string",
          },
          {
            name: "product.variants[0].inventory_quantity",
            customName: "product_stock",
            type: "number",
          },
          {
            name: "product.images[0].src",
            customName: "product_image",
            type: "string",
          },
          {
            name: "product.variants[0].grams",
            customName: "product_weight",
            type: "number",
          },
        ],
      ),

      // 3. AI taxonomy suggestion
      aiNode(
        "3",
        "AI suggest Google product taxonomy",
        520,
        "You are a Google Merchant Center taxonomy specialist. Given a product title, type, description, and brand, suggest the most appropriate Google Product Category (full path and ID). Also validate the title follows Google best practices (brand + product + key specs, under 150 chars) and suggest improvements. Respond JSON: {google_category_id: string, google_category_path: string, title_improvement: string, title_valid: string, missing_attributes: string, taxonomy_confidence: string}",
        "Title: {product_title} | Type: {product_type} | Brand: {product_brand} | Description: {product_description} | SKU: {product_sku}",
        [
          {
            field: "google_category_id",
            as: "google_category_id",
            type: "string",
          },
          {
            field: "google_category_path",
            as: "google_category_path",
            type: "string",
          },
          {
            field: "title_improvement",
            as: "title_improvement",
            type: "string",
          },
          { field: "title_valid", as: "title_valid", type: "string" },
          {
            field: "missing_attributes",
            as: "missing_attributes",
            type: "string",
          },
          {
            field: "taxonomy_confidence",
            as: "taxonomy_confidence",
            type: "string",
          },
        ],
      ),

      // 4. Attribute validation condition
      n("4", "condition", "All required attributes present?", 760, {
        conditionData: [
          {
            variable: "title_valid",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Merchant API insert/patch
      n("5", "hitApi", "Insert/patch product in Merchant Center", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/products",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "offerId", value: "{product_id}" },
            { key: "title", value: "{title_improvement}" },
            { key: "description", value: "{product_description}" },
            {
              key: "link",
              value:
                "https://{shopify_store}.myshopify.com/products/{product_handle}",
            },
            { key: "imageLink", value: "{product_image}" },
            {
              key: "price",
              value:
                '{"value":"{product_price}","currency":"{store_currency}"}',
            },
            { key: "availability", value: "{product_stock}" },
            { key: "brand", value: "{product_brand}" },
            { key: "googleProductCategory", value: "{google_category_id}" },
            { key: "condition", value: "new" },
          ],
          variables: [
            { name: "id", customName: "merchant_product_id", type: "string" },
            {
              name: "destinations[0].status",
              customName: "destination_status",
              type: "string",
            },
          ],
        },
      }),

      // 6. Disapproval monitoring
      n("6", "runCode", "Check for disapprovals", 1240, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check the destination status for disapprovals
const status = vars.destination_status || "approved";
const result = {
  is_disapproved: status === "disapproved" ? "yes" : "no",
  disapproval_reason: status === "disapproved" ? "Check Google Merchant Center for details" : "none",
  sync_status: status === "approved" ? "live" : status === "pending" ? "pending_review" : "disapproved",
  needs_team_attention: status === "disapproved" ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "is_disapproved", type: "string" },
            { name: "disapproval_reason", type: "string" },
            { name: "sync_status", type: "string" },
            { name: "needs_team_attention", type: "string" },
          ],
        },
      }),

      // 7. Product team notification (Slack)
      slackNode(
        "7",
        "Slack: Product sync notification",
        1480,
        "Merchant Center Sync:\\nProduct: {product_title} ({product_sku})\\nCategory: {google_category_path}\\nPrice: ${product_price}\\nStatus: {sync_status}\\nDisapproved: {is_disapproved}\\nMissing: {missing_attributes}\\nConfidence: {taxonomy_confidence}",
      ),

      // 8. Sync logging (Sheets)
      sheetsNode(
        "8",
        "Log product sync to Sheets",
        1720,
        "MerchantProductSync",
        '[["{product_id}","{product_title}","{product_sku}","{google_category_id}","{product_price}","{sync_status}","{is_disapproved}","{taxonomy_confidence}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // valid -> publish
      e("4", "7", "output_2"), // invalid -> alert team, skip publish
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 404. GOOGLE MERCHANT PROMOTIONAL FEED (8 nodes)
  // ===================================================================

  "Google Merchant Promotional Feed": {
    summary:
      "Manages promotional feeds in Google Merchant Center. On promo activation, validates products and pricing, formats the promotion for Merchant Center, publishes it, verifies acceptance, schedules end-date rollback, alerts marketing on Slack, and audits every promo sync.",
    nodes: [
      trigger("Webhook: Promotion activated", "webhook_app", {
        source: "Promotion Management / Shopify",
        event: "promotion.activated",
        howItStarts:
          "A promotion is activated in the promotion management system or Shopify, triggering a Merchant Center promotional feed update",
        setup:
          "Connect your promotion management system or Shopify discount events to POST promotion data (promo ID, discount type, products, start/end dates) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Product/price validation (runCode)
      n("2", "runCode", "Validate promo products and pricing", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate promotion parameters and eligible products
const promoType = vars.promo_type || "percent_off";
const discountValue = parseFloat(vars.discount_value) || 0;
const startDate = vars.promo_start_date || new Date().toISOString().split("T")[0];
const endDate = vars.promo_end_date || "";
const productIds = (vars.promo_product_ids || "").split(",").filter(Boolean);
const result = {
  validated_product_count: productIds.length,
  promo_display: promoType === "percent_off" ? discountValue + "% off" : "$" + discountValue + " off",
  promo_start_iso: startDate + "T00:00:00Z",
  promo_end_iso: endDate ? endDate + "T23:59:59Z" : "",
  has_end_date: endDate ? "yes" : "no",
  promo_id_merchant: "promo_" + (vars.promo_id || Date.now()),
  promo_destination: "Shopping_ads,Free_listings",
  validation_status: productIds.length > 0 && discountValue > 0 ? "valid" : "invalid"
};
return result;`,
          outputVariables: [
            { name: "validated_product_count", type: "number" },
            { name: "promo_display", type: "string" },
            { name: "promo_start_iso", type: "string" },
            { name: "promo_end_iso", type: "string" },
            { name: "has_end_date", type: "string" },
            { name: "promo_id_merchant", type: "string" },
            { name: "promo_destination", type: "string" },
            { name: "validation_status", type: "string" },
          ],
        },
      }),

      // 3. Merchant promo format
      n("3", "runCode", "Format Merchant Center promotion", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Format the promotion for Google Merchant Center Promotions API
const result = {
  promo_payload: JSON.stringify({
    promotionId: vars.promo_id_merchant,
    productApplicability: vars.validated_product_count > 0 ? "SPECIFIC_PRODUCTS" : "ALL_PRODUCTS",
    offerType: "GENERIC_CODE",
    genericRedemptionCode: vars.promo_code || "",
    longTitle: vars.promo_title || vars.promo_display,
    promotionEffectiveDates: vars.promo_start_iso + "/" + vars.promo_end_iso,
    redemptionChannel: ["ONLINE"],
    promotionDestinations: (vars.promo_destination || "").split(",")
  }),
  formatted_status: "ready_to_publish"
};
return result;`,
          outputVariables: [
            { name: "promo_payload", type: "string" },
            { name: "formatted_status", type: "string" },
          ],
        },
      }),

      // 4. Promotion publish
      n("4", "hitApi", "Publish promotion to Merchant Center", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://shoppingcontent.googleapis.com/content/v2.1/{merchant_id}/promotions",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "promotion", value: "{promo_payload}" }],
          variables: [
            {
              name: "promotionId",
              customName: "published_promo_id",
              type: "string",
            },
            {
              name: "promotionStatus.destinationStatuses[0].status",
              customName: "promo_status",
              type: "string",
            },
          ],
        },
      }),

      // 5. Acceptance verification
      n("5", "runCode", "Verify promotion acceptance", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check promotion acceptance status
const status = vars.promo_status || "pending";
const result = {
  promo_accepted: status === "approved" || status === "APPROVED" ? "yes" : "no",
  promo_review_status: status,
  needs_attention: status === "disapproved" || status === "DISAPPROVED" ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "promo_accepted", type: "string" },
            { name: "promo_review_status", type: "string" },
            { name: "needs_attention", type: "string" },
          ],
        },
      }),

      // 6. End-date rollback scheduling (delay)
      n("6", "delayAction", "Schedule end-date rollback", 1240, {
        delayValue: 1,
        delayUnit: "days",
        description:
          "Waits until the promotion end date to trigger cleanup/rollback of the promotional feed entry",
      }),

      // 7. Marketing Slack alert
      slackNode(
        "7",
        "Slack: Promo sync alert for marketing",
        1480,
        "Merchant Center Promo Update:\\nPromo: {promo_id_merchant}\\nDiscount: {promo_display}\\nProducts: {validated_product_count}\\nStatus: {promo_review_status}\\nAccepted: {promo_accepted}\\nDates: {promo_start_iso} to {promo_end_iso}",
      ),

      // 8. Promo sync audit (Sheets)
      sheetsNode(
        "8",
        "Log promo sync audit to Sheets",
        1720,
        "MerchantPromoAudit",
        '[["{promo_id_merchant}","{promo_display}","{validated_product_count}","{promo_start_iso}","{promo_end_iso}","{promo_review_status}","{promo_accepted}","{needs_attention}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 405. HIGH CHURN-PREDICTION RESCUE (9 nodes)
  // ===================================================================

  "High Churn-Prediction Rescue": {
    summary:
      "Rescues high churn-risk customers with multi-channel intervention. When a customer crosses the high-risk threshold, assembles their health profile, uses AI to infer churn reasons, routes VIPs to human outreach via HubSpot and standard customers to automated rescue emails, sends SMS touchpoints, gates rescue offers for approval, and logs outcomes against prediction scores.",
    nodes: [
      trigger("Webhook: High churn-risk threshold", "webhook_app", {
        source: "Churn Prediction Model / Analytics",
        event: "churn.risk.high",
        howItStarts:
          "A customer's churn prediction score crosses the high-risk threshold (e.g., >80%)",
        setup:
          "Connect your churn prediction model or analytics platform to POST high-risk customer data (customer ID, churn score, usage metrics, account age, plan details) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Health profile assembly
      n("2", "runCode", "Assemble customer health profile", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble a comprehensive health profile for churn rescue
const churnScore = parseFloat(vars.churn_score) || 85;
const loginFrequency = parseInt(vars.login_count_30d) || 0;
const ticketCount = parseInt(vars.support_tickets_30d) || 0;
const accountAge = parseInt(vars.account_age_days) || 0;
const mrr = parseFloat(vars.monthly_revenue) || 0;
const isVip = mrr >= 500 || vars.plan_tier === "enterprise";
const result = {
  health_score: Math.max(0, 100 - churnScore),
  is_vip: isVip ? "yes" : "no",
  engagement_level: loginFrequency > 10 ? "active" : loginFrequency > 3 ? "declining" : "dormant",
  support_burden: ticketCount > 3 ? "high" : ticketCount > 0 ? "moderate" : "none",
  tenure_segment: accountAge > 365 ? "veteran" : accountAge > 90 ? "established" : "new",
  revenue_at_risk: "$" + mrr.toFixed(2) + "/mo",
  risk_factors: [
    loginFrequency <= 3 ? "low_engagement" : null,
    ticketCount > 3 ? "high_support_burden" : null,
    accountAge < 90 ? "early_lifecycle" : null
  ].filter(Boolean).join(", ")
};
return result;`,
          outputVariables: [
            { name: "health_score", type: "number" },
            { name: "is_vip", type: "string" },
            { name: "engagement_level", type: "string" },
            { name: "support_burden", type: "string" },
            { name: "tenure_segment", type: "string" },
            { name: "revenue_at_risk", type: "string" },
            { name: "risk_factors", type: "string" },
          ],
        },
      }),

      // 3. AI churn reason inference
      aiNode(
        "3",
        "AI infer churn reasons",
        520,
        "You are a customer success analyst. Given the customer health profile, churn score, engagement metrics, and support history, infer the most likely reasons for churn and recommend a rescue strategy. Differentiate between VIP and standard approaches. Respond JSON: {primary_churn_reason: string, secondary_reasons: string, rescue_strategy: string, rescue_email_subject: string, rescue_email_body: string, sms_touchpoint: string, human_outreach_brief: string, rescue_offer: string}",
        "Churn score: {churn_score} | Health: {health_score} | VIP: {is_vip} | Engagement: {engagement_level} | Support burden: {support_burden} | Tenure: {tenure_segment} | Revenue at risk: {revenue_at_risk} | Risk factors: {risk_factors} | Plan: {plan_tier} | Customer: {customer_email}",
        [
          {
            field: "primary_churn_reason",
            as: "primary_churn_reason",
            type: "string",
          },
          {
            field: "secondary_reasons",
            as: "secondary_reasons",
            type: "string",
          },
          { field: "rescue_strategy", as: "rescue_strategy", type: "string" },
          {
            field: "rescue_email_subject",
            as: "rescue_email_subject",
            type: "string",
          },
          {
            field: "rescue_email_body",
            as: "rescue_email_body",
            type: "string",
          },
          { field: "sms_touchpoint", as: "sms_touchpoint", type: "string" },
          {
            field: "human_outreach_brief",
            as: "human_outreach_brief",
            type: "string",
          },
          { field: "rescue_offer", as: "rescue_offer", type: "string" },
        ],
      ),

      // 4. VIP vs standard routing condition
      n("4", "condition", "VIP customer requiring human outreach?", 760, {
        conditionData: [
          {
            variable: "is_vip",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Human outreach task (HubSpot) — VIP path
      n("5", "hitApi", "Create HubSpot outreach task for VIP", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"URGENT: VIP Churn Rescue - {customer_email}","hs_task_body":"{human_outreach_brief}\\nChurn Score: {churn_score}\\nRevenue at Risk: {revenue_at_risk}\\nPrimary Reason: {primary_churn_reason}\\nRescue Strategy: {rescue_strategy}","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED","hs_task_type":"CALL"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_task_id", type: "string" },
          ],
        },
      }),

      // 6. Automated rescue email — standard path
      emailNode(
        "6",
        "Send automated rescue email",
        1000,
        "{customer_email}",
        "{rescue_email_subject}",
        "{rescue_email_body}",
        460,
      ),

      // 7. SMS touchpoint (Twilio)
      n("7", "sendSMS", "SMS rescue touchpoint (Twilio)", 1240, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message: "{sms_touchpoint}",
        },
      }),

      // 8. Rescue offer approval gate
      n(
        "8",
        "approvalGate",
        "Approve rescue offer for at-risk customer",
        1480,
        {
          approvalGateData: {
            message:
              "Churn rescue offer needs approval:\\nCustomer: {customer_email}\\nChurn Score: {churn_score}%\\nRevenue at Risk: {revenue_at_risk}\\nPrimary Reason: {primary_churn_reason}\\nProposed Offer: {rescue_offer}\\nStrategy: {rescue_strategy}\\n\\nApprove to extend this rescue offer?",
          },
        },
      ),

      // 9. Outcome vs score logging (Sheets)
      sheetsNode(
        "9",
        "Log rescue outcome to Sheets",
        1720,
        "ChurnRescueLog",
        '[["{customer_email}","{churn_score}","{health_score}","{is_vip}","{primary_churn_reason}","{rescue_strategy}","{rescue_offer}","{revenue_at_risk}","{engagement_level}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // VIP -> human outreach task
      e("4", "6", "output_2"), // standard -> automated email
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 406. HIGH-AOV CART RECOVERY (9 nodes)
  // ===================================================================

  "High-AOV Cart Recovery": {
    summary:
      "Recovers high-value abandoned carts with premium treatment. Classifies the cart into a premium tier, checks BNPL display eligibility, generates AI-powered premium recovery copy, adds shipping and concierge incentives, sends a premium email, follows up with named-rep SMS, creates a CRM deal in HubSpot, and logs AOV tier recovery metrics.",
    nodes: [
      trigger("Webhook: High-AOV cart abandoned", "webhook_app", {
        source: "Shopify / E-commerce Platform",
        event: "cart.abandoned",
        howItStarts:
          "A high-value cart (above the AOV threshold) is abandoned, triggering a premium recovery sequence",
        setup:
          "Connect your Shopify store or e-commerce platform to POST cart abandonment data (cart total, items, customer email, customer history) to the workflow URL. Set AOV threshold in workflow settings.",
        inputFields: [],
      }),

      // 2. Premium tier classification
      n("2", "runCode", "Classify premium tier", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Classify the cart into premium tiers for differentiated treatment
const cartTotal = parseFloat(vars.cart_total) || 0;
const customerLifetimeValue = parseFloat(vars.customer_ltv) || 0;
const orderCount = parseInt(vars.customer_order_count) || 0;
let tier, repName, conciergeEligible;
if (cartTotal >= 1000) {
  tier = "ultra_premium";
  repName = vars.dedicated_rep_name || "Senior Concierge";
  conciergeEligible = true;
} else if (cartTotal >= 500) {
  tier = "premium";
  repName = vars.account_rep_name || "Personal Shopper";
  conciergeEligible = true;
} else {
  tier = "elevated";
  repName = "Customer Care";
  conciergeEligible = false;
}
const result = {
  premium_tier: tier,
  assigned_rep: repName,
  concierge_eligible: conciergeEligible ? "yes" : "no",
  bnpl_relevant: cartTotal >= 200 ? "yes" : "no",
  cart_value_display: "$" + cartTotal.toFixed(2),
  loyalty_status: orderCount >= 5 ? "loyal" : orderCount >= 2 ? "returning" : "new",
  free_shipping_eligible: cartTotal >= 150 ? "yes" : "no"
};
return result;`,
          outputVariables: [
            { name: "premium_tier", type: "string" },
            { name: "assigned_rep", type: "string" },
            { name: "concierge_eligible", type: "string" },
            { name: "bnpl_relevant", type: "string" },
            { name: "cart_value_display", type: "string" },
            { name: "loyalty_status", type: "string" },
            { name: "free_shipping_eligible", type: "string" },
          ],
        },
      }),

      // 3. BNPL display condition
      n("3", "condition", "Show BNPL payment option?", 520, {
        conditionData: [
          {
            variable: "bnpl_relevant",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI premium recovery copy
      aiNode(
        "4",
        "AI generate premium recovery copy",
        760,
        "You are a luxury e-commerce copywriter. Write a high-touch cart recovery email for a premium customer. Include BNPL mention if relevant, concierge offer if eligible, free shipping confirmation, and personal rep introduction. The tone should be exclusive, not desperate. Respond JSON: {subject_line: string, email_body: string, bnpl_section: string, concierge_offer: string, sms_from_rep: string, shipping_incentive: string}",
        "Customer: {customer_email} | Cart: {cart_value_display} | Items: {cart_items} | Tier: {premium_tier} | Rep: {assigned_rep} | BNPL: {bnpl_relevant} | Concierge: {concierge_eligible} | Loyalty: {loyalty_status} | Free shipping: {free_shipping_eligible}",
        [
          { field: "subject_line", as: "premium_subject", type: "string" },
          { field: "email_body", as: "premium_body", type: "string" },
          { field: "bnpl_section", as: "bnpl_section", type: "string" },
          { field: "concierge_offer", as: "concierge_offer", type: "string" },
          { field: "sms_from_rep", as: "sms_from_rep", type: "string" },
          {
            field: "shipping_incentive",
            as: "shipping_incentive",
            type: "string",
          },
        ],
      ),

      // 5. Shipping/concierge incentives assembly
      n("5", "runCode", "Assemble premium incentives", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assemble the final incentive package for premium recovery
const tier = vars.premium_tier || "elevated";
const incentives = [];
if (vars.free_shipping_eligible === "yes") incentives.push("Complimentary express shipping");
if (vars.concierge_eligible === "yes") incentives.push("Personal concierge assistance from " + vars.assigned_rep);
if (tier === "ultra_premium") incentives.push("Priority processing and gift wrapping");
if (tier === "premium" || tier === "ultra_premium") incentives.push("Extended 60-day return window");
const result = {
  incentive_package: incentives.join(" | "),
  incentive_count: incentives.length,
  final_email_body: vars.premium_body + "\\n\\n" + (vars.bnpl_relevant === "yes" ? vars.bnpl_section + "\\n\\n" : "") + vars.shipping_incentive + "\\n\\n" + (vars.concierge_eligible === "yes" ? vars.concierge_offer : "")
};
return result;`,
          outputVariables: [
            { name: "incentive_package", type: "string" },
            { name: "incentive_count", type: "number" },
            { name: "final_email_body", type: "string" },
          ],
        },
      }),

      // 6. Premium email
      emailNode(
        "6",
        "Send premium recovery email",
        1240,
        "{customer_email}",
        "{premium_subject}",
        "{final_email_body}",
      ),

      // 7. Named-rep SMS (Twilio)
      n("7", "sendSMS", "SMS from named rep (Twilio)", 1480, {
        sendSmsDetails: {
          to: "{customer_phone}",
          message: "{sms_from_rep}",
        },
      }),

      // 8. CRM deal creation (HubSpot)
      n("8", "hitApi", "Create HubSpot deal for high-AOV cart", 1720, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/deals",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"dealname":"Cart Recovery: {customer_email} ({cart_value_display})","amount":"{cart_total}","pipeline":"default","dealstage":"appointmentscheduled","cart_tier":"{premium_tier}","assigned_rep":"{assigned_rep}","recovery_type":"high_aov_abandoned"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_deal_id", type: "string" },
          ],
        },
      }),

      // 9. AOV tier recovery logging (Sheets)
      sheetsNode(
        "9",
        "Log AOV recovery to Sheets",
        1960,
        "HighAOVRecovery",
        '[["{customer_email}","{cart_value_display}","{premium_tier}","{assigned_rep}","{bnpl_relevant}","{concierge_eligible}","{incentive_count}","{loyalty_status}","{hubspot_deal_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // BNPL relevant -> include in copy
      e("3", "4", "output_2"), // no BNPL -> copy without BNPL
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 407. HIGH-INTENT BROWSE TO CONCIERGE OR CHAT (8 nodes)
  // ===================================================================

  "High-Intent Browse to Concierge or Chat": {
    summary:
      "Proactively engages high-intent browsers with concierge-level service. Detects high-intent behavior, scores intent via runCode, enriches customer value from HubSpot, routes by threshold to proactive chat invitation or AI product advisor, creates CRM follow-up tasks, and logs intent conversion metrics.",
    nodes: [
      trigger("Webhook: High-intent behavior detected", "webhook_app", {
        source: "Website Analytics / CDP",
        event: "visitor.high_intent",
        howItStarts:
          "A visitor exhibits high-intent signals (repeated product views, comparison behavior, pricing page visits, long session duration)",
        setup:
          "Connect your website analytics or CDP to POST high-intent visitor data (visitor ID, email, pages viewed, session duration, product views, comparison actions) to the workflow URL.",
        inputFields: [],
      }),

      // 2. Intent scoring (runCode)
      n("2", "runCode", "Score visitor intent", 280, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score intent based on behavioral signals
const productViews = parseInt(vars.product_view_count) || 0;
const pricingViews = parseInt(vars.pricing_page_views) || 0;
const comparisons = parseInt(vars.comparison_actions) || 0;
const sessionMinutes = parseInt(vars.session_duration_minutes) || 0;
const returnVisits = parseInt(vars.return_visit_count) || 0;
let score = 0;
score += Math.min(productViews * 5, 25);
score += Math.min(pricingViews * 15, 30);
score += Math.min(comparisons * 10, 20);
score += Math.min(Math.floor(sessionMinutes / 2), 15);
score += Math.min(returnVisits * 5, 10);
const result = {
  intent_score: Math.min(score, 100),
  intent_level: score >= 70 ? "very_high" : score >= 50 ? "high" : score >= 30 ? "moderate" : "low",
  top_signal: pricingViews >= 2 ? "pricing_focus" : comparisons >= 2 ? "comparison_shopping" : productViews >= 5 ? "deep_browse" : "general_interest",
  concierge_eligible: score >= 70 ? "yes" : "no",
  most_viewed_product: vars.most_viewed_product_id || "",
  session_quality: sessionMinutes >= 10 ? "extended" : sessionMinutes >= 5 ? "engaged" : "brief"
};
return result;`,
          outputVariables: [
            { name: "intent_score", type: "number" },
            { name: "intent_level", type: "string" },
            { name: "top_signal", type: "string" },
            { name: "concierge_eligible", type: "string" },
            { name: "most_viewed_product", type: "string" },
            { name: "session_quality", type: "string" },
          ],
        },
      }),

      // 3. Customer value enrichment (HubSpot)
      n("3", "hitApi", "Enrich customer value from HubSpot", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.hubapi.com/crm/v3/objects/contacts/{visitor_email}?idProperty=email&properties=lifecyclestage,hs_analytics_num_page_views,total_revenue",
          headers: [{ key: "Authorization", value: "Bearer {hubspot_token}" }],
          body: [],
          variables: [
            {
              name: "properties.lifecyclestage",
              customName: "lifecycle_stage",
              type: "string",
            },
            {
              name: "properties.total_revenue",
              customName: "customer_total_revenue",
              type: "string",
            },
            { name: "id", customName: "hubspot_contact_id", type: "string" },
          ],
        },
      }),

      // 4. Threshold condition
      n("4", "condition", "Meets concierge threshold?", 760, {
        conditionData: [
          {
            variable: "concierge_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 5. Proactive chat invitation
      n("5", "sendMessage", "Proactive chat invitation", 1000, {
        sendMessageDetails: {
          conversationId: "{session_id}",
          message:
            "Hi {visitor_name}! I noticed you have been looking at some great products. I am your personal shopping concierge. Can I help you find exactly what you are looking for or answer any questions?",
        },
      }),

      // 6. AI product advisor
      aiNode(
        "6",
        "AI product advisor response",
        1000,
        "You are a knowledgeable product advisor. Based on the visitor's browsing behavior (most viewed products, comparison actions, pricing interest), generate a helpful, non-pushy chat message that offers specific product insights, addresses likely questions, and guides toward a purchase decision. Respond JSON: {advisor_message: string, product_insight: string, recommended_action: string, follow_up_question: string}",
        "Intent: {intent_level} (score: {intent_score}) | Top signal: {top_signal} | Most viewed: {most_viewed_product} | Session: {session_quality} | Lifecycle: {lifecycle_stage} | Revenue: {customer_total_revenue} | Products browsed: {products_browsed}",
        [
          { field: "advisor_message", as: "advisor_message", type: "string" },
          { field: "product_insight", as: "product_insight", type: "string" },
          {
            field: "recommended_action",
            as: "recommended_action",
            type: "string",
          },
          {
            field: "follow_up_question",
            as: "follow_up_question",
            type: "string",
          },
        ],
        460,
      ),

      // 7. CRM follow-up task
      n("7", "hitApi", "Create HubSpot follow-up task", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"hs_task_subject":"Follow up: High-intent browser {visitor_email}","hs_task_body":"Intent score: {intent_score} ({intent_level})\\nTop signal: {top_signal}\\nMost viewed: {most_viewed_product}\\nSession: {session_quality}\\nRecommended action: {recommended_action}","hs_task_priority":"HIGH","hs_task_status":"NOT_STARTED","hs_task_type":"TODO"}',
            },
          ],
          variables: [
            { name: "id", customName: "followup_task_id", type: "string" },
          ],
        },
      }),

      // 8. Intent conversion logging (Sheets)
      sheetsNode(
        "8",
        "Log intent conversion to Sheets",
        1480,
        "HighIntentConversion",
        '[["{visitor_email}","{intent_score}","{intent_level}","{top_signal}","{most_viewed_product}","{session_quality}","{concierge_eligible}","{lifecycle_stage}","{recommended_action}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // concierge eligible -> proactive chat
      e("4", "6", "output_2"), // below threshold -> AI advisor
      e("5", "7"),
      e("6", "7"),
      e("7", "8"),
    ],
  },
};
